import { Fragment } from 'react';
import { useNavigate } from 'react-router';
import { useLocalize } from '@hooks/use-localize';
import { DashRegistrationStep, historyLinkMap } from '@/types/dashboard';
import type { IHistoryProgressModel } from '@/types/history/history-progress';
import BackToDashboard from '@/components/chrome/back-to-dashboard';

interface IHistoryProgressProps {
    model: IHistoryProgressModel;
    overrideStep?: DashRegistrationStep;
}

interface ISubSection {
    label: string;
    anchor: string;
    show: boolean;
}

interface IProgressStep {
    step: DashRegistrationStep;
    isComplete: boolean;
    label: string;
    subSections: ISubSection[];
}

/**
 * The check mark, or an empty slot of the same width, so every label in the list starts
 * at the same x. Only the main steps track completion — the sub-sections are anchors
 * within a step's page, so they always get the empty slot.
 */
const StepIcon = ({ isComplete }: { isComplete: boolean }) =>
    isComplete ? (
        <i className="fas fa-check-circle progress-list__icon" aria-hidden="true"></i>
    ) : (
        <span className="progress-list__icon" aria-hidden="true"></span>
    );

const HistoryProgress = ({ model, overrideStep }: IHistoryProgressProps) => {
    const t = useLocalize(model.languageData);
    const navigate = useNavigate();

    const steps: IProgressStep[] = [];

    // The Demographics step is only part of the history flow for quick-start donors.
    if (model.isQuickStartDonor) {
        steps.push({
            step: DashRegistrationStep.HistoryDemographics,
            isComplete: model.isHistoryDemographicsComplete,
            label: t('DashLabel_Demographics', 'Demographics'),
            subSections: [
                // { label: t('DashLabel_AddressInformation', 'Address'), anchor: 'address', show: true },
                // { label: t('DashLabel_ContactInformation', 'Contact'), anchor: 'contact', show: true },
                // { label: t('DashLabel_Lifestyle', 'Lifestyle'), anchor: 'lifestyle', show: true },
                // { label: t('DashLabel_InsurancePhysician', 'Insurance & Physician'), anchor: 'insurance-physician', show: true },
            ],
        });
    }

    steps.push(
        {
            step: DashRegistrationStep.HistoryDiagnosed,
            isComplete: model.isHistoryDiagnosedComplete,
            label: t('DashLabel_MedicalHistory', 'Medical History'),
            subSections: [
                // { label: t('DashLabel_BloodTypeAndCardiovascular', 'Blood Type & Cardiovascular'), anchor: 'cardiovascular', show: true },
                // { label: t('DashLabel_Respiratory', 'Respiratory'), anchor: 'respiratory', show: true },
                // { label: t('DashLabel_GeneralHealth', 'General Health'), anchor: 'conditions', show: true },
                // { label: t('DashLabel_WomensHealth', "Women's Health"), anchor: 'womens-health', show: model.isFemale },
                // { label: t('DashLabel_OtherConditions', 'Other Conditions'), anchor: 'other-conditions', show: true },
                // { label: t('DashLabel_Screenings', 'Screenings'), anchor: 'screenings', show: true },
            ],
        },
        {
            step: DashRegistrationStep.HistoryFamily,
            isComplete: model.isHistoryFamilyComplete,
            label: t('DashLabel_FamilyHistory', 'Family History'),
            subSections: [
                // { label: t('DashLabel_FamilyConditions', 'Conditions'), anchor: 'conditions', show: true },
                // { label: t('DashLabel_Parents', 'Parents'), anchor: 'parents', show: true },
                // { label: t('DashLabel_SiblingsAndChildren', 'Siblings & Children'), anchor: 'household', show: true },
            ],
        },
        {
            step: DashRegistrationStep.HistoryMisc,
            isComplete: model.isHistoryMiscComplete,
            label: t('DashLabel_AllergiesSurgeriesMeds', 'Allergies/Surgeries/Meds'),
            subSections: [
            ],
        },
        {
            step: DashRegistrationStep.HistorySocial,
            isComplete: model.isHistorySocialComplete,
            label: t('DashLabel_SocialHistory', 'Social History'),
            subSections: [],
        },
        {
            step: DashRegistrationStep.HistorySummary,
            isComplete: model.isHistorySummaryComplete,
            label: t('DashLabel_HistorySummary', 'History Summary'),
            subSections: [],
        },
    );

    const stepToCheck = overrideStep ? overrideStep : model.currentRegistrationStep;

    return (
        <div className="history-progress">
            {model.isIE && (
                <div className="alert alert-warning">
                    {t(
                        'Alert_BrowserRecommend',
                        'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                    )}
                    .
                </div>
            )}

            <h5 className="history-progress__title" id="progress">
                {t('DashLabel_HistoryQuestions', 'History Questions')}:
            </h5>

            <div className="progress-list">
                {steps.map((item) => {
                    const isActive = stepToCheck === item.step;
                    const isLink = item.step < model.currentRegistrationStep || item.isComplete;
                    const linkStep = historyLinkMap.find((x) => x.step === item.step);
                    const stepBase = linkStep?.link;

                    const className =
                        'progress-list__item' +
                        (isActive
                            ? ' progress-list__item--active'
                            : item.isComplete
                                ? ' progress-list__item--complete'
                                : '');

                    const content = (
                        <>
                            <StepIcon isComplete={item.isComplete && !isActive} />
                            {item.label}
                        </>
                    );

                    return (
                        <Fragment key={item.step}>
                            {isLink ? (
                                <a className={className + ' clickable'} onClick={() => navigate(stepBase ?? '')}>
                                    {content}
                                </a>
                            ) : (
                                <span className={className}>{content}</span>
                            )}

                            {item.subSections.filter(x => x.show).map((sub) => (
                                <a
                                    key={sub.anchor}
                                    className="progress-list__item progress-list__item--child clickable"
                                    onClick={() => navigate(stepBase + '#' + sub.anchor)}
                                >
                                    <StepIcon isComplete={false} />
                                    {sub.label}
                                </a>
                            ))}
                        </Fragment>
                    );
                })}
            </div>

            <div style={{ paddingTop: 20 }}>
                <BackToDashboard
                    link={null}
                    t={t}
                />
            </div>
        </div>
    );
};

export default HistoryProgress;
